import emailjs from "@emailjs/browser";
import { EMAILJS_CONFIG } from "../config/emailjs";
import { BASE_URL } from "../config/site";

const GIFT_VOUCHER_EMAIL_IMAGE_URL = `${BASE_URL}/images/gift1.png`;

export interface EmailData {
  to_email: string;
  from_email: string;
  subject: string;
  message: string;
  site_name: string;
}

export interface PaymentConfirmationData {
  customer_email: string;
  customer_name: string;
  workshop_title: string;
  workshop_date: string;
  workshop_time?: string;
  tickets_count: number;
  total_amount: number;
  transaction_id: string;
  site_name: string;
}

export interface ContactAutoReplyStrings {
  subject: string;
  title: string;
  greeting: string;
  body: string;
  yourMessage: string;
  regards: string;
  teamLabel: string;
  footer: string;
  defaultName: string;
}

export interface GiftVoucherEmailStrings {
  subject: string;
  title: string;
  greeting: string;
  body: string;
  codeLabel: string;
  participantsLabel: string;
  howToUse: string;
  regards: string;
  teamLabel: string;
  footer: string;
  defaultName: string;
}

export interface GiftVoucherEmailData {
  customer_email: string;
  purchaser_name?: string;
  code: string;
  participants: number;
  amount: number;
  site_name: string;
}

export const emailService = {
  sendContactEmail: async (emailData: EmailData): Promise<void> => {
    try {
      const templateParams = {
        email: emailData.to_email,
        from_email: emailData.from_email,
        subject: emailData.subject,
        message: emailData.message,
        site_name: emailData.site_name,
      };

      
      const response = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ADMIN_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY,
      );

      if (response.status !== 200) {
        throw new Error(`Failed to send email. Status: ${response.status}`);
      }
    } catch (error) {
                  throw new Error("שגיאה בשליחת המייל. נסה שוב מאוחר יותר.");
    }
  },

  sendAutoReply: async (
    userEmail: string,
    siteName: string,
    userName: string | undefined,
    userMessage: string | undefined,
    emailStrings: ContactAutoReplyStrings,
    language: "he" | "ru",
  ): Promise<void> => {
    try {
      const name = userName || emailStrings.defaultName;
      const safeUserMessage = (userMessage || "")
        .replace(/<[^>]+>/g, "")
        .trim();
      const escapeHtml = (s: string) =>
        s
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");
      const isRtl = language === "he";
      const dir = isRtl ? "rtl" : "ltr";
      const textAlign = isRtl ? "right" : "left";
      const messageBorder = isRtl
        ? "border-right: 4px solid #4f2e03"
        : "border-left: 4px solid #4f2e03";
      const messageBlock = safeUserMessage
        ? `<div style="margin-top: 14px; padding: 12px 16px; background: #f5f5f5; border-radius: 8px; ${messageBorder};">
          <div style="font-size: 15px; color: #4f2e03; font-weight: 600; margin-bottom: 6px;">${escapeHtml(emailStrings.yourMessage)}</div>
          <div style="font-size: 16px; color: #333; line-height: 1.5;">${escapeHtml(safeUserMessage)}</div>
        </div>`
        : "";
      const bodyHtml = `
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="font-family: 'Noto Sans Hebrew', Arial, sans-serif; max-width: 100%;">
  <tr><td style="padding: 10px;" align="center">
    <div style="max-width: 560px; width: 100%; margin: 0 auto; direction: ${dir}; text-align: ${textAlign}; box-sizing: border-box;">
      <div style="background: #fff; border-radius: 16px; box-shadow: 0 2px 16px rgba(0,0,0,0.08); overflow: hidden; border-top: 4px solid #4f2e03;">
        <div style="padding: 18px 16px;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td align="center" style="padding-bottom: 10px;"><span style="font-size: 28px;">✉️</span></td></tr></table>
          <h2 style="margin: 0 0 10px; font-size: 22px; color: #4f2e03; font-weight: 700; line-height: 1.3;">${escapeHtml(emailStrings.title)}</h2>
          <p style="margin: 0 0 8px; font-size: 17px; color: #333; line-height: 1.5;">${escapeHtml(emailStrings.greeting)} ${escapeHtml(name)},</p>
          <p style="margin: 0 0 12px; font-size: 17px; color: #333; line-height: 1.5;">${escapeHtml(emailStrings.body)}</p>
          ${messageBlock}
          <p style="margin: 18px 0 0; font-size: 17px; color: #333;">${escapeHtml(emailStrings.regards)}</p>
          <p style="margin: 4px 0 0; font-size: 17px; color: #4f2e03; font-weight: 600;">${escapeHtml(emailStrings.teamLabel)} ${escapeHtml(siteName)}</p>
        </div>
        <div style="padding: 12px 16px; background: #f5f5dc; font-size: 13px; color: #666;">${escapeHtml(emailStrings.footer)}</div>
      </div>
    </div>
  </td></tr>
</table>`;
      const subject = emailStrings.subject.replace("{{siteName}}", siteName);
      const templateParams = {
        email: userEmail,
        to_email: userEmail,
        customer_email: userEmail,
        site_name: siteName,
        subject,
        body: bodyHtml,
        message: safeUserMessage,
      };

      const response = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_CUSTOMER_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY,
      );

      if (response.status !== 200) {
        throw new Error(
          `Failed to send auto-reply email. Status: ${response.status}`,
        );
      }
    } catch (error) {
                  // Don't throw error for auto-reply as it's not critical
    }
  },

  sendGiftVoucherEmail: async (
    voucherData: GiftVoucherEmailData,
    emailStrings: GiftVoucherEmailStrings,
    language: "he" | "ru",
  ): Promise<void> => {
    try {
      const name = voucherData.purchaser_name || emailStrings.defaultName;
      const escapeHtml = (s: string) =>
        s
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");
      const isRtl = language === "he";
      const dir = isRtl ? "rtl" : "ltr";
      const textAlign = isRtl ? "right" : "left";
      const detailsBorder = isRtl
        ? "border-right: 4px solid #4f2e03"
        : "border-left: 4px solid #4f2e03";

      const participantsLine = emailStrings.participantsLabel.replace(
        "{{count}}",
        String(voucherData.participants),
      );

      const giftImageAlt =
        language === "he" ? "שובר מתנה" : "Подарочный сертификат";

      const bodyHtml = `
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="font-family: 'Noto Sans Hebrew', Arial, sans-serif; max-width: 100%;">
  <tr><td style="padding: 8px;" align="center">
    <div style="max-width: 560px; width: 100%; margin: 0 auto; direction: ${dir}; text-align: ${textAlign}; box-sizing: border-box;">
      <div style="background: #fff; border-radius: 16px; box-shadow: 0 2px 16px rgba(0,0,0,0.08); overflow: hidden; border-top: 4px solid #4f2e03;">
        <div style="padding: 14px 14px 12px;">
          <h2 style="margin: 0 0 6px; font-size: 22px; color: #4f2e03; font-weight: 700; line-height: 1.25;">${escapeHtml(emailStrings.title)}</h2>
          <p style="margin: 0 0 4px; font-size: 17px; color: #333; line-height: 1.4;">${escapeHtml(emailStrings.greeting)} ${escapeHtml(name)},</p>
          <p style="margin: 0 0 8px; font-size: 17px; color: #333; line-height: 1.4;">${escapeHtml(emailStrings.body)}</p>
          <div style="margin: 6px 0 8px; text-align: center; line-height: 0;">
            <img src="${GIFT_VOUCHER_EMAIL_IMAGE_URL}" alt="${escapeHtml(giftImageAlt)}" width="280" style="display: block; max-width: 100%; width: 280px; height: auto; margin: 0 auto; border-radius: 12px; border: 0;" />
          </div>
          <div style="margin: 0 0 8px; padding: 12px 14px; background: #f5f5dc; border-radius: 12px; text-align: center;">
            <div style="font-size: 14px; color: #4f2e03; font-weight: 600; margin-bottom: 4px;">${escapeHtml(emailStrings.codeLabel)}</div>
            <div style="font-size: 26px; letter-spacing: 3px; font-family: 'Courier New', monospace; color: #4f2e03; font-weight: 700; line-height: 1.2;">${escapeHtml(voucherData.code)}</div>
          </div>
          <div style="margin: 0 0 8px; padding: 10px 14px; background: #f5f5f5; border-radius: 8px; ${detailsBorder};">
            <div style="font-size: 16px; color: #333; line-height: 1.45;">${escapeHtml(participantsLine)}</div>
            <div style="font-size: 15px; color: #666; line-height: 1.45; margin-top: 4px;">${escapeHtml(emailStrings.howToUse)}</div>
          </div>
          <p style="margin: 8px 0 0; font-size: 17px; color: #333; line-height: 1.4;">${escapeHtml(emailStrings.regards)}</p>
          <p style="margin: 2px 0 0; font-size: 17px; color: #4f2e03; font-weight: 600; line-height: 1.4;">${escapeHtml(emailStrings.teamLabel)} ${escapeHtml(voucherData.site_name)}</p>
        </div>
        <div style="padding: 10px 14px; background: #f5f5dc; font-size: 13px; color: #666; line-height: 1.4;">${escapeHtml(emailStrings.footer)}</div>
      </div>
    </div>
  </td></tr>
</table>`;

      const subject = emailStrings.subject.replace(
        "{{siteName}}",
        voucherData.site_name,
      );
      const templateParams = {
        email: voucherData.customer_email,
        to_email: voucherData.customer_email,
        customer_email: voucherData.customer_email,
        site_name: voucherData.site_name,
        subject,
        body: bodyHtml,
        message: "",
      };

      const response = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_CUSTOMER_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY,
      );

      if (response.status !== 200) {
        throw new Error(
          `Failed to send gift voucher email. Status: ${response.status}`,
        );
      }
    } catch (error) {
                  // Don't throw - email failure should not block the voucher purchase
    }
  },

  sendPaymentConfirmation: async (
    paymentData: PaymentConfirmationData,
  ): Promise<void> => {
        
    try {
      const templateParams = {
        customer_email: paymentData.customer_email,
        customer_name: paymentData.customer_name,
        workshop_title: paymentData.workshop_title,
        workshop_date: paymentData.workshop_date,
        workshop_time: paymentData.workshop_time || "",
        tickets_count: paymentData.tickets_count,
        total_amount: paymentData.total_amount,
        transaction_id: paymentData.transaction_id,
        site_name: paymentData.site_name,
        // משתנים נוספים לתאימות עם תבניות שונות
        to_email: paymentData.customer_email,
        email: paymentData.customer_email,
        name: paymentData.customer_name,
        amount: paymentData.total_amount,
        date: paymentData.workshop_date,
        time: paymentData.workshop_time || "",
        tickets: paymentData.tickets_count,
        transaction: paymentData.transaction_id,
      };

      const response = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_PAYMENT_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY,
      );

      if (response.status !== 200) {
        throw new Error(
          `Failed to send payment confirmation email. Status: ${response.status}`,
        );
      }
    } catch (error) {
                  // Don't throw error for payment confirmation as it's not critical for the payment flow
          }
  },
};
